import {
    ReactNode,
    useEffect,
    useState,
} from 'react';

import PropTypes from 'prop-types';
import {fetchUserAttributes} from 'aws-amplify/auth';
import {useAuth} from "../hooks/useAuth.ts";
import {signOutViaBroker} from "../utils/signOutViaBroker.ts";
import {UserAttributesContext} from "../context/UserAttributesContext.tsx";

type UserAttributesProviderProps = {
    children?: ReactNode;
}

export const UserAttributesProvider = ({
                                           children,
                                       }: UserAttributesProviderProps) => {


    const [givenName, setGivenName] = useState('')
    const [familyName, setFamilyName] = useState('')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [companyName, setCompanyName] = useState('')
    const [tier, setTier] = useState('')

    const {
        user,
        status
    } = useAuth()

    // FNP-1947: fetchUserAttributes calls fetchAuthSession, which fetches
    // identity-pool credentials. Started while AuthProvider's own session fetch
    // is still in flight, Amplify does not share it and runs a second GetId and
    // GetCredentialsForIdentity round. Waiting for 'authenticated' (the session
    // has settled) lets it reuse the cached credentials.
    const authenticated = status === 'authenticated'


    useEffect(() => {
        const fetchAttributes = async () => {
            if (!user || !authenticated) {
                return
            }

            // FNP-958: Cognito user attributes are string-valued and every consumer
            // below feeds them to a setState<string>, so `string` is the accurate
            // type — `any` was hiding that the shape was already known.
            let attributes = {} as { [key: string]: string | undefined }
            try {
                attributes = await fetchUserAttributes();
            } catch (e: unknown) {
                if (e instanceof Error) {
                    switch (e.name) {
                        case 'NotAuthorizedException':
                            console.debug('Message', e.message)
                            if (e.message.includes('Token is inactive')) {
                                await signOutViaBroker()
                            }
                            break
                    }
                }
            }


            if (Array.isArray(Object.keys(attributes)) && Object.keys(attributes).length > 0) {

                Object.keys(attributes)
                      .forEach((key: string) => {

                          switch (key) {
                              case 'given_name':
                                  setGivenName(attributes[key] ?? '')
                                  break
                              case 'family_name':
                                  setFamilyName(attributes[key] ?? '')
                                  break
                              case 'name':
                                  setName(attributes[key] ?? '')
                                  break
                              case 'email':
                                  setEmail(attributes[key] ?? '')
                                  break
                              case 'custom:company_name':
                                  setCompanyName(attributes[key] ?? '')
                                  break
                              case 'custom:tier':
                                  setTier(attributes[key] ?? '')
                                  break
                          }
                      })

            }

        }

        fetchAttributes()

    }, [user, authenticated])


    // FNP-1936: always the same element. Returning bare children while signed
    // out swapped the tree's shape when the user resolved, which remounted the
    // permissions provider below and re-ran its check.
    return (
        <UserAttributesContext.Provider
            value={{
                givenName,
                familyName,
                name,
                email,
                companyName,
                tier
            }}
        >
            {children}
        </UserAttributesContext.Provider>
    );
};

UserAttributesProvider.propTypes = {
    children: PropTypes.node,
};

